import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  X, 
  PackagePlus, 
  SlidersHorizontal, 
  ArrowRightLeft, 
  MapPin, 
  ToggleLeft, 
  AlertCircle, 
  CheckCircle2, 
  Building2 
} from "lucide-react";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;

const ManageStockModal = ({ 
  isOpen, 
  onClose, 
  product, 
  productList = [], 
  warehouses = [], 
  onStockUpdated,
  initialTab = "add"
}) => {
  const [activeTab, setActiveTab] = useState(initialTab || "add");
  const [currentSelectedProduct, setCurrentSelectedProduct] = useState(product);
  const [quantity, setQuantity] = useState("");
  const [targetStock, setTargetStock] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [destinationWarehouse, setDestinationWarehouse] = useState("");
  const [rackBinLocation, setRackBinLocation] = useState("");
  const [stockStatus, setStockStatus] = useState("active");
  const [adjustmentReason, setAdjustmentReason] = useState("Physical Count Audit");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen]);

  useEffect(() => {
    if (product) {
      setCurrentSelectedProduct(product);
    } else if (productList.length > 0) {
      setCurrentSelectedProduct(productList[0]);
    }
  }, [product, productList, isOpen]);

  const activeProduct = currentSelectedProduct || product;

  useEffect(() => {
    if (activeProduct) {
      setSelectedWarehouse(activeProduct.warehouse || (warehouses[0]?.name) || "Bhubaneswar (Main)");
      setRackBinLocation(activeProduct.rackBinLocation || "A-01-01");
      setStockStatus(activeProduct.stock_status || "active");
      setQuantity("");
      setTargetStock(activeProduct.stock || 0);
      setNote("");
      const diffWh = warehouses.find(w => w.name !== activeProduct.warehouse);
      setDestinationWarehouse(diffWh ? diffWh.name : "");
    }
  }, [activeProduct, warehouses, isOpen]);

  if (!isOpen || !activeProduct) return null;

  const currentStock = Number(activeProduct.stock) || 0;
  const prodName = activeProduct.name?.en || (typeof activeProduct.name === "string" ? activeProduct.name : "Product");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let payload = {
        productId: activeProduct._id,
        action: activeTab,
        warehouse: selectedWarehouse,
        note: note.trim() || undefined,
        performedBy: "Admin",
      };

      if (activeTab === "add") {
        const qty = parseInt(quantity, 10);
        if (!qty || qty <= 0) {
          toast.error("Please enter a valid positive quantity to add");
          setSubmitting(false);
          return;
        }
        payload.quantity = qty;
        payload.note = note.trim() || `Restocked +${qty} units into ${selectedWarehouse}`;
      } else if (activeTab === "adjust") {
        const newQty = parseInt(targetStock, 10);
        if (isNaN(newQty) || newQty < 0) {
          toast.error("Please enter a valid stock level (0 or greater)");
          setSubmitting(false);
          return;
        }
        payload.action = "set";
        payload.quantity = newQty;
        payload.note = `${adjustmentReason}${note ? ` - ${note}` : ""}`;
      } else if (activeTab === "transfer") {
        const qty = parseInt(quantity, 10);
        if (!qty || qty <= 0) {
          toast.error("Please enter a valid quantity to transfer");
          setSubmitting(false);
          return;
        }
        if (qty > currentStock) {
          toast.error(`Cannot transfer more than available stock (${currentStock} units)`);
          setSubmitting(false);
          return;
        }
        if (!destinationWarehouse || destinationWarehouse === selectedWarehouse) {
          toast.error("Please select a different destination warehouse");
          setSubmitting(false);
          return;
        }
        payload.quantity = qty;
        payload.warehouseTo = destinationWarehouse;
        payload.note = note.trim() || `Inter-warehouse transfer from ${selectedWarehouse} to ${destinationWarehouse}`;
      } else if (activeTab === "relocate") {
        payload.rackBinLocation = rackBinLocation.trim();
        payload.note = note.trim() || `Storage relocated to ${selectedWarehouse} [${rackBinLocation}]`;
      } else if (activeTab === "status") {
        payload.status = stockStatus;
        payload.note = note.trim() || `Stock status updated to ${stockStatus}`;
      }

      const res = await axios.post(`${API}/inventory/manage-stock`, payload);
      if (res.data.success) {
        toast.success(res.data.message || "Stock managed successfully!");
        if (onStockUpdated) onStockUpdated(res.data.product);
        onClose();
      } else {
        toast.error(res.data.message || "Failed to update stock");
      }
    } catch (err) {
      console.error("Stock management error:", err);
      toast.error(err.response?.data?.message || "Error managing stock");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Amazon-Style Header */}
        <div className="bg-gradient-to-r from-[#131921] to-[#232f3e] px-5 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-[#febd69]">
              <PackagePlus className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-medium text-[#febd69] uppercase tracking-wider">
                Amazon-Style Inventory Hub
              </div>
              <h3 className="text-base font-semibold text-white leading-tight">
                Manage Stock & Allocation
              </h3>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Product Identity Summary Strip */}
        <div className="bg-[#f7fafa] border-b border-gray-200 px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-12 h-12 rounded border border-gray-200 bg-white p-1 shrink-0 flex items-center justify-center">
              {activeProduct.images?.[0] ? (
                <img src={activeProduct.images[0]} alt="product" className="max-w-full max-h-full object-contain" />
              ) : (
                <Building2 className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              {productList.length > 1 ? (
                <select
                  value={activeProduct._id}
                  onChange={(e) => {
                    const found = productList.find((p) => p._id === e.target.value);
                    if (found) setCurrentSelectedProduct(found);
                  }}
                  className="text-sm font-semibold text-gray-900 border border-gray-300 rounded px-2 py-1 bg-white max-w-full truncate focus:outline-none focus:ring-1 focus:ring-[#b07d17]"
                >
                  {productList.map((p) => {
                    const pName = p.name?.en || (typeof p.name === "string" ? p.name : "Product");
                    return (
                      <option key={p._id} value={p._id}>
                        {pName} ({p.sku || p.bhkg_id || "No SKU"}) - Stock: {p.stock || 0}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <p className="text-sm font-semibold text-gray-900 truncate" title={prodName}>
                  {prodName}
                </p>
              )}
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                  SKU: {activeProduct.sku || "--"}
                </span>
                <span>•</span>
                <span className="font-mono text-[#007185]">
                  ID: {activeProduct.bhkg_id || "--"}
                </span>
              </div>
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="text-xs text-gray-500">Current Stock</div>
            <div className={`text-lg font-bold ${currentStock > 20 ? "text-green-700" : currentStock > 0 ? "text-amber-700" : "text-red-700"}`}>
              {currentStock} <span className="text-xs font-normal text-gray-500">units</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 px-3 text-xs sm:text-sm font-medium overflow-x-auto">
          <button
            onClick={() => setActiveTab("add")}
            className={`py-3 px-3 flex items-center gap-1.5 border-b-2 whitespace-nowrap transition ${
              activeTab === "add"
                ? "border-[#b07d17] text-[#8c6312] font-semibold bg-white"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <PackagePlus size={16} /> Add Stock
          </button>
          <button
            onClick={() => setActiveTab("adjust")}
            className={`py-3 px-3 flex items-center gap-1.5 border-b-2 whitespace-nowrap transition ${
              activeTab === "adjust"
                ? "border-[#b07d17] text-[#8c6312] font-semibold bg-white"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <SlidersHorizontal size={16} /> Adjust Stock
          </button>
          <button
            onClick={() => setActiveTab("transfer")}
            className={`py-3 px-3 flex items-center gap-1.5 border-b-2 whitespace-nowrap transition ${
              activeTab === "transfer"
                ? "border-[#b07d17] text-[#8c6312] font-semibold bg-white"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <ArrowRightLeft size={16} /> Transfer Hub
          </button>
          <button
            onClick={() => setActiveTab("relocate")}
            className={`py-3 px-3 flex items-center gap-1.5 border-b-2 whitespace-nowrap transition ${
              activeTab === "relocate"
                ? "border-[#b07d17] text-[#8c6312] font-semibold bg-white"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <MapPin size={16} /> Bin Location
          </button>
          <button
            onClick={() => setActiveTab("status")}
            className={`py-3 px-3 flex items-center gap-1.5 border-b-2 whitespace-nowrap transition ${
              activeTab === "status"
                ? "border-[#b07d17] text-[#8c6312] font-semibold bg-white"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <ToggleLeft size={16} /> Stock Status
          </button>
        </div>

        {/* Tab Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* TAB 1: ADD STOCK */}
          {activeTab === "add" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Quantity to Add <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 50"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                  <span className="absolute right-3 top-2.5 text-xs text-gray-400">units</span>
                </div>
                {quantity && parseInt(quantity, 10) > 0 && (
                  <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                    <CheckCircle2 size={13} />
                    New Available Stock will be: <strong>{currentStock + parseInt(quantity, 10)} units</strong>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Receiving Warehouse
                </label>
                <select
                  value={selectedWarehouse}
                  onChange={(e) => setSelectedWarehouse(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                >
                  {warehouses.map((wh) => (
                    <option key={wh._id || wh.warehouseId} value={wh.name}>
                      {wh.name} ({wh.warehouseId || wh.city})
                    </option>
                  ))}
                  {warehouses.length === 0 && (
                    <option value="Bhubaneswar (Main)">Bhubaneswar (Main) Central Fulfilment Hub</option>
                  )}
                </select>
              </div>
            </div>
          )}

          {/* TAB 2: ADJUST STOCK */}
          {activeTab === "adjust" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    New Absolute Stock Level <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={targetStock}
                    onChange={(e) => setTargetStock(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Net Stock Variance
                  </label>
                  <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-semibold text-gray-700 flex items-center justify-between">
                    <span>Variance:</span>
                    <span className={Number(targetStock) - currentStock >= 0 ? "text-green-700" : "text-red-700"}>
                      {Number(targetStock) - currentStock >= 0 ? `+${Number(targetStock) - currentStock}` : Number(targetStock) - currentStock} units
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Adjustment Reason
                </label>
                <select
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                >
                  <option value="Physical Count Audit">Physical Count Audit (Cycle Count)</option>
                  <option value="Damaged / Defective Stock">Damaged / Defective Stock</option>
                  <option value="Missing / Inventory Shrinkage">Missing / Inventory Shrinkage</option>
                  <option value="Supplier Replacement">Supplier Replacement</option>
                  <option value="Return to Vendor">Return to Vendor</option>
                  <option value="Correction of Entry Error">Correction of Entry Error</option>
                </select>
              </div>
            </div>
          )}

          {/* TAB 3: TRANSFER WAREHOUSE */}
          {activeTab === "transfer" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Source Warehouse (From)
                  </label>
                  <input
                    type="text"
                    disabled
                    value={selectedWarehouse}
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-100 rounded-lg text-sm text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Destination Warehouse (To) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={destinationWarehouse}
                    onChange={(e) => setDestinationWarehouse(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                  >
                    <option value="">Select Destination...</option>
                    {warehouses
                      .filter((wh) => wh.name !== selectedWarehouse)
                      .map((wh) => (
                        <option key={wh._id || wh.warehouseId} value={wh.name}>
                          {wh.name} ({wh.warehouseId || wh.city})
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Quantity to Transfer (Max: {currentStock}) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max={currentStock}
                  placeholder={`1 to ${currentStock}`}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                />
              </div>
            </div>
          )}

          {/* TAB 4: RELOCATE STORAGE */}
          {activeTab === "relocate" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Assigned Warehouse
                </label>
                <select
                  value={selectedWarehouse}
                  onChange={(e) => setSelectedWarehouse(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                >
                  {warehouses.map((wh) => (
                    <option key={wh._id || wh.warehouseId} value={wh.name}>
                      {wh.name} ({wh.warehouseId})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Bin / Rack Code (e.g. Zone-Rack-Shelf-Bin)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Z1-R04-S2-B12"
                  value={rackBinLocation}
                  onChange={(e) => setRackBinLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Used for warehouse picking, packing and inventory audit routes.
                </p>
              </div>
            </div>
          )}

          {/* TAB 5: STOCK STATUS */}
          {activeTab === "status" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Listing Stock Status
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { val: "active", label: "Active / Good", border: "border-green-500", text: "text-green-700 bg-green-50" },
                    { val: "inactive", label: "Inactive / Hold", border: "border-amber-500", text: "text-amber-700 bg-amber-50" },
                    { val: "out of stock", label: "Out of Stock", border: "border-red-500", text: "text-red-700 bg-red-50" },
                  ].map((s) => (
                    <button
                      key={s.val}
                      type="button"
                      onClick={() => setStockStatus(s.val)}
                      className={`p-3 rounded-lg border text-center transition font-semibold text-xs ${
                        stockStatus === s.val
                          ? `${s.border} ${s.text} shadow-sm ring-2 ring-[#b07d17]/30`
                          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Note / Audit Trail */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Internal Note / Reference (Audit Log)
            </label>
            <input
              type="text"
              placeholder="e.g. PO-8493 restocking from supplier or cycle count note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
            />
          </div>

          {/* Action Buttons */}
          <div className="pt-3 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-[#b07d17] hover:bg-[#8c6312] text-white rounded-lg text-sm font-semibold shadow-sm transition disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? "Updating..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageStockModal;
